import React from 'react';
import autoBind from 'react-autobind';
import QRCode from 'qrcode';
import { saveAs } from 'file-saver';

import {
  Modal,
  Button,
  Text,
  Box,
  Group,
  Stack,
  ActionIcon,
  SegmentedControl,
} from '@mantine/core';
import { IconX, IconDownload } from '@tabler/icons-react';

import Localization from '../../Localization.jsx';
import Config from '../../Config.jsx';

class QRCodeDialog extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.canvasRef = React.createRef();
    // Two separate codes (teacher picks the student's platform). Neither needs
    // the redirect server: iOS uses the raw scheme (handled by iOS Camera),
    // Android uses a self-contained intent:// URL (handled by Chrome/Android).
    this.state = { platform: 'ios' };
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open && this.props.localeId) {
      setTimeout(() => this.generateQR(), 100);
    }
  }

  // The deep link encoded for the currently selected platform.
  currentUrl() {
    const { localeId } = this.props;
    return this.state.platform === 'android'
      ? Config.EPACK_ANDROID_URL(localeId)
      : Config.EPACK_IOS_URL(localeId);
  }

  handlePlatformChange(platform) {
    this.setState({ platform }, () => this.generateQR());
  }

  generateQR() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    QRCode.toCanvas(canvas, this.currentUrl(), {
      width: 300,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    }, (err) => {
      if (err) console.error('QR generation error:', err);
    });
  }

  handleDownload() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    const suffix = this.state.platform === 'android' ? 'Android' : 'iOS';
    canvas.toBlob((blob) => {
      if (blob) {
        const name = this.props.localeName
          ? `${this.props.localeName}_${suffix}_QR.png`
          : `locale_${this.props.localeId}_${suffix}_QR.png`;
        saveAs(blob, name);
      }
    }, 'image/png');
  }

  render() {
    const { open, onClose, localeName } = this.props;
    const isAndroid = this.state.platform === 'android';

    return (
      <Modal
        opened={open}
        onClose={onClose}
        size="xs"
        centered
        zIndex={2000}
        withCloseButton={false}
        title={(
          <Group justify="space-between" w="100%">
            <Text fw={600}>{Localization.strings.QR_CODE}</Text>
            <ActionIcon variant="subtle" color="gray" onClick={onClose}>
              <IconX size={18} />
            </ActionIcon>
          </Group>
        )}
      >
        <Stack gap="md" align="center">
          {localeName && (
            <Text size="sm" fw={500} ta="center">
              {localeName}
            </Text>
          )}
          <SegmentedControl
            fullWidth
            value={this.state.platform}
            onChange={this.handlePlatformChange}
            data={[
              { label: 'iOS', value: 'ios' },
              { label: 'Android', value: 'android' },
            ]}
          />
          <Box
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 4,
              padding: 16,
              backgroundColor: '#ffffff',
            }}
          >
            <canvas ref={this.canvasRef} />
          </Box>
          <Text size="xs" fw={500} ta="center">
            {isAndroid
              ? Localization.strings.QR_CODE_PLATFORM_HINT_ANDROID
              : Localization.strings.QR_CODE_PLATFORM_HINT_IOS}
          </Text>
          <Text size="xs" c="dimmed" ta="center">
            {Localization.strings.QR_CODE_DESC}
          </Text>
          <Text
            size="xs"
            c="dimmed"
            ta="center"
            style={{ fontFamily: 'monospace', wordBreak: 'break-all', maxWidth: '100%' }}
          >
            {this.currentUrl()}
          </Text>
          <Group justify="center" mt="sm">
            <Button
              leftSection={<IconDownload size={18} />}
              onClick={this.handleDownload}
            >
              {Localization.strings.DOWNLOAD_QR_CODE}
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }
}

export default QRCodeDialog;
